import type { ReactNode } from 'react'
import { CopyValue } from '@/components/analytics/UnitPiecePreview'
import {
  formatKopecks,
  formatPercent,
  formatPerGram,
  perGram,
  signClass,
  unitChargeAmount,
  unitPenaltyAmount,
  unitServiceAmount,
  UNIT_SERVICE_CHARGE_KINDS,
  type UnitEconomicsRow,
} from '@/lib/unit-economics'

const SOURCE = {
  netting: 'отчёт Маркета о платежах',
  onec: '1С',
  cdek: 'СДЭК',
} as const

function day(isoDate: string | null): string {
  if (!isoDate) return '—'
  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
}

function Step({ label, value, sign = '', note, source, weight, strong, tone, children }: {
  label: string
  value: number | null
  sign?: '−' | '+' | ''
  note?: ReactNode
  source?: string
  weight: number | null
  strong?: boolean
  tone?: string
  children?: ReactNode
}) {
  const shown = value === null ? '—' : `${sign}${formatKopecks(value)}`
  const gram = value === null ? null : perGram(sign === '−' ? -value : value, weight)
  return (
    <div className="py-[3px]">
      <div className="flex items-baseline justify-between gap-3">
        <span className={strong ? 'font-semibold text-ink' : 'text-ink-2'}>{label}</span>
        <span className="text-right tab-nums">
          <span className={`${strong ? 'font-semibold' : ''} ${tone ?? 'text-ink'}`}>{shown}</span>
          <span className="ml-2 inline-block min-w-[86px] text-[11px] text-ink-faint">{gram === null ? '' : formatPerGram(gram)}</span>
        </span>
      </div>
      {note || source ? (
        <div className="text-[11px] leading-snug text-ink-faint">
          {note}{note && source ? ' · ' : ''}{source ? `источник: ${source}` : ''}
        </div>
      ) : null}
      {children}
    </div>
  )
}

function costNote(document: string | null, receiptDate: string | null): string {
  if (!document) return `поступление от ${day(receiptDate)}`
  return document.includes(' от ') ? document : `${document} от ${day(receiptDate)}`
}

function costDetails(row: UnitEconomicsRow): string | undefined {
  if (row.costSource === 'owner_default_rate') {
    return row.costReceiptDate ? `поступление ${day(row.costReceiptDate)}` : row.costReason ?? undefined
  }
  return [
    row.costDocument ? costNote(row.costDocument, row.costReceiptDate) : null,
    row.costReceiptDate ? `поступление ${day(row.costReceiptDate)}` : null,
  ].filter(Boolean).join(' · ') || undefined
}

function chargeLines(row: UnitEconomicsRow, kinds: ReadonlySet<string>) {
  return row.charges.filter((charge) => kinds.has(charge.kind))
}

/** One row's exact money chain: sale, Market fees, received, cost, CDEK, tax and profit. */
export function UnitBreakdown({ row }: { row: UnitEconomicsRow }) {
  const weight = row.weightGrams
  const serviceCharges = chargeLines(row, UNIT_SERVICE_CHARGE_KINDS)
  const penaltyCharges = chargeLines(row, new Set(['penalty']))
  const otherCharges = row.withholdings.length > 0
    ? row.withholdings
    : row.charges
      .filter((charge) => !UNIT_SERVICE_CHARGE_KINDS.has(charge.kind) && charge.kind !== 'boost' && charge.kind !== 'penalty')
      .map((charge) => ({ kind: charge.kind, label: charge.label, amount: charge.cash }))
  const waybills = row.cdekWaybills.map((waybill) => `№${waybill.number}${waybill.direction === 'inbound' ? ' (возврат)' : ''}`).join(', ')
  const bank = row.bankOrders.map((order) => `№${order.id} от ${day(order.date)}${order.matches ? ', сверено с выплатой' : order.matches === false ? ', не сошлось с выплатой' : ''}`).join('; ')

  return (
    <div data-testid={`unit-breakdown-${row.orderId}`} className="mt-2 border-t border-rule px-1 pt-2 text-[13px] sm:ml-[56px] sm:max-w-[640px]">
      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
        <CopyValue label="Заказ" value={row.orderId} />
        {row.article ? <CopyValue label="Артикул" value={row.article} /> : null}
        {row.uin ? <CopyValue label="УИН" value={row.uin} /> : null}
        <CopyValue label="SKU Маркета" value={row.shopSku} />
      </div>
      <div className="mb-1 text-[11px] text-ink-faint">
        {[row.program, row.tariff === 'new' ? 'новый тариф' : 'старый тариф', weight !== null ? `${weight.toLocaleString('ru-RU')} г по 1С` : 'вес не указан'].filter(Boolean).join(' · ')}
      </div>

      <Step label="Продали за" value={row.soldFor} weight={weight} source={SOURCE.netting} strong
        tone={signClass(row.soldFor)} note={row.pending ? 'ожидает выплаты' : bank || undefined} />
      <div className="my-1 text-[12px] text-ink-2">
        <div>Удержания Маркета по заказу: {formatKopecks(row.marketGross)} (деньгами {formatKopecks(row.marketMoney)}, баллами {formatKopecks(row.marketPoints)})</div>
        <div>Баллы за скидку Маркета по этому заказу: −{formatKopecks(row.marketDiscountCredit)}</div>
        <div className="font-medium">Итого удержания: {formatKopecks(row.marketWithheld)}</div>
      </div>
      <Step label="Услуги ЯМ" value={unitServiceAmount(row)} sign="−" weight={weight} source={SOURCE.netting}>
        {serviceCharges.map((charge, index) => (
          <div key={`${charge.kind}-${index}`} className="flex justify-between pl-3 text-[12px] text-ink-muted">
            <span>{charge.label}{charge.rate !== null ? ` ${formatPercent(charge.rate)}` : ''}</span>
            <span className="tab-nums">−{formatKopecks(charge.cash)}</span>
          </div>
        ))}
      </Step>
      <Step label="Буст" value={unitChargeAmount(row, 'boost') ?? 0} sign="−" weight={weight} source={SOURCE.netting} />
      <Step label="Штрафы" value={unitPenaltyAmount(row)} sign="−" weight={weight} source={SOURCE.netting}>
        {penaltyCharges.map((charge, index) => (
          <div key={`${charge.kind}-${index}`} className="flex justify-between pl-3 text-[12px] text-ink-muted">
            <span>{charge.label}</span>
            <span className="tab-nums">−{formatKopecks(charge.cash)}</span>
          </div>
        ))}
      </Step>
      <Step label="Удержания" value={otherCharges.length > 0 ? otherCharges.reduce((total, charge) => total + charge.amount, 0) : null} sign="−" weight={weight} source={SOURCE.netting}>
        {otherCharges.map((charge, index) => (
          <div key={`${charge.kind}-${index}`} className="flex justify-between pl-3 text-[12px] text-ink-muted">
            <span>{charge.label}</span>
            <span className="tab-nums">−{formatKopecks(charge.amount)}</span>
          </div>
        ))}
      </Step>
      <Step label="Получили" value={row.received} weight={weight} source={SOURCE.netting} />
      <Step label="Деньгами по этому заказу" value={Math.abs(row.cash)} sign={row.cash < 0 ? '−' : ''} weight={weight} source={SOURCE.netting} />
      {row.costSource === 'owner_default_rate' ? (
        <Step label="Купили за" value={row.cost} sign="−" weight={weight} note={costDetails(row)} />
      ) : (
        <Step label="Купили за" value={row.cost} sign="−" weight={weight} source={SOURCE.onec}
          note={row.cost !== null ? costDetails(row) : [row.costReason, row.costOnecReason ? `в 1С: ${row.costOnecReason}` : null].filter(Boolean).join(' · ')} />
      )}
      <Step label="Маржа" value={row.margin} weight={weight} strong tone={signClass(row.margin)} />
      <Step label="СДЭК" value={row.cdekCost} sign={row.cdekCost ? '−' : ''} weight={weight} source={SOURCE.cdek}
        note={row.cdekReason ?? (waybills ? `накладная ${waybills}` : undefined)} />
      <Step label="Налог" value={row.tax} sign={row.tax ? '−' : ''} weight={weight}
        note={row.taxReason ?? row.taxLabel} />
      <Step label="Прибыль" value={row.profit} weight={weight} strong tone={signClass(row.profit)}
        note={row.profit === null ? row.profitReason : undefined} />
    </div>
  )
}
